import { formatRelativeDate } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { type MouseEvent } from 'react';

import { Text } from '@/components/ui';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { type HydratedFile } from '@/hooks/useHydratedFile';
import { cn } from '@/lib/utils';

import { spriteSubtitle } from './sprite-presentation';
import { SpriteActionsMenu } from './SpriteActionsMenu';
import { SpritePaperThumb } from './SpritePaperThumb';
import { type SpriteSummary } from './sprites.store';
import { useSpriteItem } from './use-sprite-item';

interface SpriteListRowProps {
  sprite: SpriteSummary;
  hydrated: HydratedFile;
  onOpen: (sprite: SpriteSummary, event?: MouseEvent) => void;
}

export const SpriteListRow = observer(
  ({ sprite, hydrated, onOpen }: SpriteListRowProps) => {
    const {
      artifact,
      title,
      KindIcon,
      isMenuOpen,
      setIsMenuOpen,
      interactiveProps,
    } = useSpriteItem(sprite, hydrated, onOpen);

    return (
      <div className="group relative">
        <XStack
          className={cn(
            'min-w-0 gap-4 items-center',
            'p-2 rounded-2xl',
            'cursor-default',
            'focus-ring-inset',
            'group-hover:bg-surface-hover transition-colors',
            isMenuOpen && 'bg-surface-hover'
          )}
          {...interactiveProps}
        >
          <div className="relative size-12 shrink-0 overflow-hidden rounded-xl border-[0.5px] border-border-strong bg-surface">
            <SpritePaperThumb
              artifact={artifact}
              fallbackIcon={KindIcon}
              iconClassName="size-5"
            />
          </div>

          <YStack className="flex-1 min-w-0 gap-0.5">
            <Text className="font-medium text-base truncate">{title}</Text>
            <Text className="text-xs truncate text-foreground-muted">
              {spriteSubtitle(sprite)}
            </Text>
          </YStack>

          <XStack className="shrink-0 items-center relative min-w-0 justify-end">
            <Text
              className={cn(
                'text-xs text-foreground-muted px-2 w-20 text-right pointer-events-none',
                'transition-opacity duration-150 group-hover:opacity-0 group-has-[:focus-visible]:opacity-0',
                isMenuOpen && 'opacity-0'
              )}
            >
              {formatRelativeDate(sprite.updated_at)}
            </Text>
            <XStack className="absolute right-2 top-1/2 -translate-y-1/2 items-center gap-0.5">
              <SpriteActionsMenu
                sprite={sprite}
                file={hydrated.file}
                onOpen={() => onOpen(sprite)}
                isMenuOpen={isMenuOpen}
                onMenuOpenChange={setIsMenuOpen}
              />
            </XStack>
          </XStack>
        </XStack>
      </div>
    );
  }
);
